import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Trash2, Clock, Zap, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Badge, Button, Select, Label, FormGroup, Flex,
  ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter,
  Spinner, LoadingContainer, EmptyStateAction,
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

// ── Helpers de expressão cron ─────────────────────────────────────────────────

const DAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildCron({ hour, minute, days }) {
  // days: array de 0-6 (0=Dom)
  if (!days || days.length === 0 || days.length === 7) {
    return `cron(${minute} ${hour} * * ? *)`;
  }
  // AWS EventBridge usa SUN,MON,... ou 1,2,...
  const dayNames = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
  const selected = days.map(d => dayNames[d]).join(',');
  return `cron(${minute} ${hour} ? * ${selected} *)`;
}

function describeCron({ hour, minute, days }, lang = 'pt-BR') {
  const isPT = lang.startsWith('pt');
  const pad = n => String(n).padStart(2, '0');
  const time = `${pad(hour)}:${pad(minute)}`;
  const dayLabels = isPT ? DAYS_PT : DAYS_EN;

  if (!days || days.length === 0 || days.length === 7) {
    return isPT ? `Todo dia às ${time}` : `Every day at ${time}`;
  }
  const names = days.sort((a, b) => a - b).map(d => dayLabels[d]).join(', ');
  return isPT ? `${names} às ${time}` : `${names} at ${time}`;
}

// ── Presets de automação ──────────────────────────────────────────────────────

function getPresets(t) {
  return [
    {
      id: 'agenda',
      icon: '📅',
      label: t('automations.preset.agenda', { defaultValue: 'Resumo da agenda' }),
      prompt: t('automations.preset.agendaPrompt', { defaultValue: 'Envie um resumo da minha agenda de hoje com os compromissos do Google Calendar.' }),
      defaultHour: 8, defaultMinute: 0,
      defaultDays: [1, 2, 3, 4, 5], // seg-sex
    },
    {
      id: 'reminder',
      icon: '🔔',
      label: t('automations.preset.reminder', { defaultValue: 'Lembrete personalizado' }),
      prompt: '',
      defaultHour: 9, defaultMinute: 0,
      defaultDays: [1, 2, 3, 4, 5],
    },
    {
      id: 'finance',
      icon: '💰',
      label: t('automations.preset.finance', { defaultValue: 'Resumo financeiro' }),
      prompt: t('automations.preset.financePrompt', { defaultValue: 'Envie um resumo dos meus gastos desta semana registrados na planilha.' }),
      defaultHour: 20, defaultMinute: 0,
      defaultDays: [1], // segunda
    },
    {
      id: 'custom',
      icon: '⚡',
      label: t('automations.preset.custom', { defaultValue: 'Personalizado' }),
      prompt: '',
      defaultHour: 9, defaultMinute: 0,
      defaultDays: [1, 2, 3, 4, 5],
    },
  ];
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function AutomationsPage() {
  const { t, i18n } = useTranslation(['tasks', 'common']);
  const { tenantId } = useParams();
  const toast   = useToast();
  const confirm = useConfirm();

  const [automations, setAutomations] = useState([]); // conectores cron ativos
  const [agents, setAgents]           = useState([]);
  const [tierObj, setTierObj]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [deletingId, setDeletingId]   = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const breadcrumbItems = [
    { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` },
    { label: t('automations.title', { defaultValue: 'Automações' }) },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsRes, connectorsRes, tenantRes, tiersRes] = await Promise.all([
        api.get(`/marketplace/tenants/${tenantId}/agents`),
        api.get(`/connectors/${tenantId}`),
        api.get(`/tenants/${tenantId}`),
        api.get('/public/tiers'),
      ]);
      const installedAgents = agentsRes.data.agents || [];
      setAgents(installedAgents);

      // Filtra apenas conectores cron
      const cronConnectors = (connectorsRes.data.connectors || []).filter(c => c.type === 'cron');
      // Enriquece com nome do agente
      setAutomations(cronConnectors.map(c => ({
        ...c,
        agentName: installedAgents.find(a => a.instanceId === c.agentInstanceId)?.name || c.agentInstanceId,
        agentIcon: installedAgents.find(a => a.instanceId === c.agentInstanceId)?.icon || '🤖',
      })));

      // Tier do tenant
      const tier = tenantRes.data?.tier;
      const tiers = tiersRes.data?.tiers || [];
      setTierObj(tiers.find(ti => ti.tierId === tier) || null);
    } catch (e) { toast.error(t('saveError', { ns: 'common' })); }
    finally { setLoading(false); }
  }, [tenantId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (connector) => {
    const ok = await confirm({
      title: t('automations.deleteConfirm', { defaultValue: 'Remover esta automação?' }),
      danger: true,
    });
    if (!ok) return;
    setDeletingId(connector.connectorId);
    try {
      await api.delete(`/connectors/${tenantId}/${connector.connectorId}`);
      toast.success(t('automations.deleteSuccess', { defaultValue: 'Automação removida' }));
      load();
    } catch { toast.error(t('saveError', { ns: 'common' })); }
    finally { setDeletingId(null); }
  };

  // Limite dinâmico do plano
  const scheduledLimit = tierObj?.limits?.scheduledTasks ?? null;
  const scheduledEnabled = tierObj?.features?.scheduledTasks !== false;
  const atLimit = scheduledLimit !== null && scheduledLimit !== -1 && automations.length >= scheduledLimit;
  const canCreate = scheduledEnabled && !atLimit;

  return (
    <>
      <AppHeader title={t('automations.title', { defaultValue: 'Automações' })} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('automations.title', { defaultValue: 'Automações' })}</PageTitle>
            <PageSubtitle>
              {t('automations.subtitle', { defaultValue: 'Ações que seu assistente executa automaticamente em horários programados.' })}
              {scheduledLimit !== null && scheduledLimit !== -1 && (
                <span style={{ marginLeft: 8, color: atLimit ? theme.colors.danger : theme.colors.textMuted }}>
                  {automations.length}/{scheduledLimit} {t('automations.used', { defaultValue: 'usadas' })}
                </span>
              )}
            </PageSubtitle>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            disabled={!canCreate}
            title={!scheduledEnabled ? t('automations.notInPlan', { defaultValue: 'Automações não incluídas no seu plano' }) : atLimit ? t('automations.limitReached', { defaultValue: 'Limite do plano atingido' }) : undefined}
          >
            <Plus size={14} />
            {t('automations.new', { defaultValue: 'Nova automação' })}
          </Button>
        </PageHeader>

        {!scheduledEnabled && (
          <UpgradeBanner>
            <AlertTriangle size={16} />
            <span>
              {t('automations.upgradeHint', { defaultValue: 'Automações não estão incluídas no seu plano atual.' })}{' '}
              <a href={`/billing/${tenantId}`}>{t('automations.seePlans', { defaultValue: 'Ver planos' })} →</a>
            </span>
          </UpgradeBanner>
        )}

        {loading ? (
          <LoadingContainer><Spinner size="24px" /></LoadingContainer>
        ) : automations.length === 0 ? (
          <EmptyStateAction
            icon={<Zap size={48} strokeWidth={1} color={theme.colors.gray300} />}
            title={t('automations.empty', { defaultValue: 'Nenhuma automação configurada' })}
            hint={t('automations.emptyHint', { defaultValue: 'Crie uma automação para que seu assistente envie mensagens, resumos ou lembretes automaticamente.' })}
            action={canCreate ? () => setShowModal(true) : null}
            actionLabel={canCreate ? t('automations.new', { defaultValue: 'Nova automação' }) : null}
          />
        ) : (
          <AutomationList>
            {automations.map(conn => {
              const schedule = conn.cronRule || conn.schedule || '';
              return (
                <AutomationCard key={conn.connectorId}>
                  <AutomationLeft>
                    <AutomationIcon>{conn.agentIcon}</AutomationIcon>
                    <AutomationInfo>
                      <AutomationName>{conn.name || t('automations.unnamed', { defaultValue: 'Automação' })}</AutomationName>
                      <AutomationMeta>
                        <Clock size={11} />
                        {schedule
                          ? schedule.replace(/^cron\(|\)$/g, '')
                          : t('automations.noSchedule', { defaultValue: 'Sem horário' })}
                        <span style={{ color: theme.colors.border }}>·</span>
                        {conn.agentName}
                      </AutomationMeta>
                      {conn.prompt && (
                        <AutomationPrompt>{conn.prompt}</AutomationPrompt>
                      )}
                    </AutomationInfo>
                  </AutomationLeft>
                  <Flex gap={theme.spacing[2]}>
                    <Badge variant={conn.enabled ? 'success' : 'neutral'}>
                      {conn.enabled ? t('automations.active', { defaultValue: 'Ativa' }) : t('automations.inactive', { defaultValue: 'Inativa' })}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(conn)}
                      disabled={deletingId === conn.connectorId}
                      aria-label={t('automations.delete', { defaultValue: 'Remover automação' })}
                    >
                      {deletingId === conn.connectorId
                        ? <Spinner size="14px" />
                        : <Trash2 size={14} color={theme.colors.danger} />}
                    </Button>
                  </Flex>
                </AutomationCard>
              );
            })}
          </AutomationList>
        )}
      </PageContainer>

      {showModal && (
        <AutomationModal
          t={t}
          i18n={i18n}
          tenantId={tenantId}
          agents={agents}
          onSave={async (payload) => {
            await api.post(`/connectors/${tenantId}`, payload);
            toast.success(t('automations.createSuccess', { defaultValue: 'Automação criada!' }));
            setShowModal(false);
            load();
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// ── Modal de criação ──────────────────────────────────────────────────────────

function AutomationModal({ t, i18n, tenantId, agents, onSave, onClose }) {
  const presets = getPresets(t);
  const [step, setStep]         = useState(1); // 1=preset, 2=config
  const [preset, setPreset]     = useState(null);
  const [agentId, setAgentId]   = useState(agents.length === 1 ? (agents[0].instanceId) : '');
  const [hour, setHour]         = useState(9);
  const [minute, setMinute]     = useState(0);
  const [days, setDays]         = useState([1, 2, 3, 4, 5]);
  const [prompt, setPrompt]     = useState('');
  const [name, setName]         = useState('');
  const [saving, setSaving]     = useState(false);

  const selectPreset = (p) => {
    setPreset(p);
    setHour(p.defaultHour);
    setMinute(p.defaultMinute);
    setDays(p.defaultDays);
    setPrompt(p.prompt);
    setName(p.label);
    setStep(2);
  };

  const toggleDay = (d) => {
    setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a, b) => a - b));
  };

  const cronExpr   = buildCron({ hour, minute, days });
  const cronHuman  = describeCron({ hour, minute, days }, i18n.language);
  const canSave    = agentId && prompt.trim() && name.trim();

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const agent = agents.find(a => a.instanceId === agentId);
      await onSave({
        connectorId:     `cron_${Date.now()}`,
        type:            'cron',
        agentInstanceId: agentId,
        name:            name.trim(),
        enabled:         true,
        cronRule:        cronExpr,
        prompt:          prompt.trim(),
      });
    } finally { setSaving(false); }
  };

  const hours   = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 15, 30, 45];
  const dayLabels = i18n.language.startsWith('pt') ? DAYS_PT : DAYS_EN;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox maxWidth="480px" onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {step === 1
              ? t('automations.modal.choosePreset', { defaultValue: 'O que você quer automatizar?' })
              : `${preset?.icon} ${preset?.label}`}
          </ModalTitle>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('close', { ns: 'common' })}>✕</Button>
        </ModalHeader>

        <ModalBody>
          {/* Passo 1 — escolha do preset */}
          {step === 1 && (
            <PresetGrid>
              {presets.map(p => (
                <PresetCard key={p.id} onClick={() => selectPreset(p)}>
                  <PresetIcon>{p.icon}</PresetIcon>
                  <PresetLabel>{p.label}</PresetLabel>
                </PresetCard>
              ))}
            </PresetGrid>
          )}

          {/* Passo 2 — configuração */}
          {step === 2 && (
            <>
              {/* Agente — só mostra seletor se houver mais de 1 */}
              {agents.length > 1 && (
                <FormGroup>
                  <Label>{t('automations.modal.agent', { defaultValue: 'Assistente' })} *</Label>
                  <Select value={agentId} onChange={e => setAgentId(e.target.value)}>
                    <option value="">{t('automations.modal.agentPlaceholder', { defaultValue: 'Selecione o assistente...' })}</option>
                    {agents.map(a => (
                      <option key={a.instanceId} value={a.instanceId}>{a.icon} {a.name}</option>
                    ))}
                  </Select>
                </FormGroup>
              )}

              {/* Nome */}
              <FormGroup>
                <Label>{t('automations.modal.name', { defaultValue: 'Nome da automação' })} *</Label>
                <NameInput
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={t('automations.modal.namePlaceholder', { defaultValue: 'Ex: Resumo diário' })}
                  maxLength={60}
                />
              </FormGroup>

              {/* Horário */}
              <FormGroup>
                <Label>{t('automations.modal.time', { defaultValue: 'Horário' })}</Label>
                <TimeRow>
                  <Select value={hour} onChange={e => setHour(Number(e.target.value))} style={{ flex: 1 }}>
                    {hours.map(h => (
                      <option key={h} value={h}>{String(h).padStart(2, '0')}h</option>
                    ))}
                  </Select>
                  <Select value={minute} onChange={e => setMinute(Number(e.target.value))} style={{ flex: 1 }}>
                    {minutes.map(m => (
                      <option key={m} value={m}>{String(m).padStart(2, '0')}min</option>
                    ))}
                  </Select>
                </TimeRow>
              </FormGroup>

              {/* Dias da semana */}
              <FormGroup>
                <Label>{t('automations.modal.days', { defaultValue: 'Dias da semana' })}</Label>
                <DayPicker>
                  {[0, 1, 2, 3, 4, 5, 6].map(d => (
                    <DayBtn
                      key={d}
                      type="button"
                      $active={days.includes(d)}
                      onClick={() => toggleDay(d)}
                      aria-pressed={days.includes(d)}
                    >
                      {dayLabels[d]}
                    </DayBtn>
                  ))}
                </DayPicker>
                <CronPreview>
                  <Clock size={11} />
                  {cronHuman}
                </CronPreview>
              </FormGroup>

              {/* Instrução */}
              <FormGroup>
                <Label>{t('automations.modal.instruction', { defaultValue: 'O que o assistente deve fazer?' })} *</Label>
                <PromptTextarea
                  rows={4}
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder={t('automations.modal.instructionPlaceholder', { defaultValue: 'Ex: Envie um resumo da minha agenda de hoje com os compromissos do Google Calendar.' })}
                  maxLength={1000}
                />
                <CharCount $warn={prompt.length > 800}>{prompt.length}/1000</CharCount>
              </FormGroup>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {step === 2 && (
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} style={{ marginRight: 'auto' }}>
              ← {t('automations.modal.back', { defaultValue: 'Voltar' })}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('cancel', { ns: 'common' })}
          </Button>
          {step === 2 && (
            <Button variant="primary" onClick={handleSave} disabled={saving || !canSave}>
              {saving ? <Spinner size="14px" /> : t('automations.modal.create', { defaultValue: 'Criar automação' })}
            </Button>
          )}
        </ModalFooter>
      </ModalBox>
    </ModalOverlay>
  );
}

// ── Styled ────────────────────────────────────────────────────────────────────

const BreadcrumbBar = styled.div`
  background: ${theme.colors.white};
  padding: 0 ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
`;

const UpgradeBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.warningLight};
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.lg};
  font-size: ${theme.typography.sm};
  color: #92400e;
  margin-bottom: ${theme.spacing[5]};
  a { color: #92400e; font-weight: 600; }
`;

const AutomationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const AutomationCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  flex-wrap: wrap;
`;

const AutomationLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  flex: 1;
  min-width: 0;
`;

const AutomationIcon = styled.div`
  font-size: 1.75rem;
  line-height: 1;
  flex-shrink: 0;
`;

const AutomationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AutomationName = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
`;

const AutomationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const AutomationPrompt = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textSecondary};
  margin-top: ${theme.spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
`;

// Modal

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[3]};
`;

const PresetCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[5]} ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1.5px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const PresetIcon = styled.div`font-size: 2rem; line-height: 1;`;

const PresetLabel = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
  text-align: center;
`;

const TimeRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const DayPicker = styled.div`
  display: flex;
  gap: ${theme.spacing[1]};
  flex-wrap: wrap;
`;

const DayBtn = styled.button`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.radii.md};
  border: 1.5px solid ${p => p.$active ? theme.colors.primary : theme.colors.border};
  background: ${p => p.$active ? theme.colors.primary : theme.colors.white};
  color: ${p => p.$active ? 'white' : theme.colors.textSecondary};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  min-width: 36px;
  &:hover { border-color: ${theme.colors.primary}; }
  &:focus-visible { outline: 2px solid ${theme.colors.primary}; outline-offset: 2px; }
`;

const CronPreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  margin-top: ${theme.spacing[2]};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.medium};
`;

const NameInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const PromptTextarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  line-height: 1.5;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const CharCount = styled.div`
  font-size: ${theme.typography.xs};
  color: ${p => p.$warn ? theme.colors.warning : theme.colors.textMuted};
  text-align: right;
  margin-top: 2px;
`;
